import { type ButtonHTMLAttributes, type FC, useState } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CalendarIcon } from 'lucide-react';
import { type DateRange, TZDate } from 'react-day-picker';

import { Calendar } from '@shared/components/ui/calendar';
import {
  formatRange,
  getDisplayMonth,
  getUtcTimeState,
  type Preset,
  PRESETS,
  type RangeBoundaryType,
  type TimeUnitType,
} from '@shared/components/ui/custom/generic-date-range-picker/date-range-picker.utils';
import { DatePickerFooter } from '@shared/components/ui/custom/generic-date-range-picker/DatePickerFooter';
import { cn } from '@shared/utils';

import { GenericButton } from '../buttons/GenericButton';
import { GenericPopover } from '../GenericPopover';

dayjs.extend(utc);

export interface DateRangePickerProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'onChange'> {
  value?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
  className?: string;
  primaryButtonLabel?: string;
}

export const GenericDateRangePicker: FC<DateRangePickerProps> = ({
  value,
  onChange,
  className,
  primaryButtonLabel,
  ...triggerProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string | undefined>(undefined);

  const [tempDate, setTempDate] = useState<DateRange | undefined>(value);
  const [displayMonth, setDisplayMonth] = useState<Date | undefined>(() => getDisplayMonth(value?.from));
  const [startTime, setStartTime] = useState(() => getUtcTimeState(value?.from));
  const [endTime, setEndTime] = useState(() => getUtcTimeState(value?.to, 'end'));

  const handleSelect = (range: DateRange | undefined) => {
    setTempDate(range);
    setSelectedPreset(undefined);

    if (range?.from) {
      const nowUtc = dayjs.utc();
      const fromUtc = dayjs.utc(range.from);
      const isTodayUtc = fromUtc.isSame(nowUtc, 'day');
      const isEndTodayUtc = range.to ? dayjs.utc(range.to).isSame(nowUtc, 'day') : isTodayUtc;

      setStartTime({ h: '00', m: '00' });

      if (isEndTodayUtc) {
        setEndTime({ h: nowUtc.format('HH'), m: nowUtc.format('mm') });
      } else {
        setEndTime({ h: '23', m: '59' });
      }
    }
  };

  const handlePresetClick = (preset: Preset) => {
    const range = preset.getValue();

    setTempDate(range);
    setStartTime(getUtcTimeState(range.from));
    setEndTime(getUtcTimeState(range.to, 'end'));
    setSelectedPreset(preset.label);
    setDisplayMonth(getDisplayMonth(range.from));
  };

  const handleApply = () => {
    if (!tempDate?.from) return;

    const nowUtc = dayjs.utc();
    const preserveSeconds = Boolean(selectedPreset);
    const fromSource = dayjs.utc(tempDate.from);
    const toSource = dayjs.utc(tempDate.to ?? tempDate.from);

    const startDate = dayjs
      .utc(tempDate.from)
      .hour(Number.parseInt(startTime.h))
      .minute(Number.parseInt(startTime.m))
      .second(preserveSeconds ? fromSource.second() : 0)
      .millisecond(0);

    let endDate = dayjs
      .utc(tempDate.to ?? tempDate.from)
      .hour(Number.parseInt(endTime.h))
      .minute(Number.parseInt(endTime.m))
      .second(preserveSeconds ? toSource.second() : 0)
      .millisecond(0);

    const isEndTodayUtc = endDate.isSame(nowUtc, 'day');

    if (isEndTodayUtc) {
      if (endDate.isAfter(nowUtc)) endDate = nowUtc;
    }

    onChange?.({
      from: startDate.toDate(),
      to: endDate.toDate(),
    });

    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open) {
      setTempDate(value);
      setStartTime(getUtcTimeState(value?.from));
      setEndTime(getUtcTimeState(value?.to, 'end'));
      setSelectedPreset(undefined);
      setDisplayMonth(getDisplayMonth(value?.from));
    }
  };

  const handleTimeChange = (type: RangeBoundaryType, unit: TimeUnitType, val: string) => {
    const currentValue = type === 'start' ? startTime[unit] : endTime[unit];

    if (currentValue === val) {
      return;
    }
    setSelectedPreset(undefined);
    const setter = type === 'start' ? setStartTime : setEndTime;
    setter((prev) => ({ ...prev, [unit]: val }));
  };

  const handleClear = () => {
    onChange?.(undefined);

    setTempDate(undefined);
    setSelectedPreset(undefined);
    setStartTime(getUtcTimeState(undefined, 'start'));
    setEndTime(getUtcTimeState(undefined, 'end'));
    setDisplayMonth(getDisplayMonth());
  };

  const isInvalid = triggerProps['aria-invalid'] === true || triggerProps['aria-invalid'] === 'true';

  return (
    <GenericPopover
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger={
        <GenericButton
          variant="outline"
          className={cn(
            'h-10 w-full justify-start text-left bg-input dark:bg-input/30 text-sm font-normal',
            !value && 'text-muted-foreground',
            isInvalid && 'border-error!',
            isInvalid && 'ring-1! ring-error-foreground!',
            className,
          )}
          {...triggerProps}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatRange(value)}
        </GenericButton>
      }
      contentClassName="p-0 w-[--radix-popover-trigger-width] min-w-[700px]"
      align="start"
    >
      <div className="flex flex-col">
        <div className="flex">
          <div className="border-r border-border p-2 space-y-1 min-w-[120px]">
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handlePresetClick(preset)}
                className={cn(
                  'block w-full text-left px-2 py-2 text-sm hover:bg-muted rounded-md transition-colors',
                  selectedPreset === preset.label ? 'bg-muted' : '',
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="p-3 flex flex-col items-center justify-center flex-1">
            <Calendar
              mode="range"
              timeZone="UTC"
              selected={tempDate}
              onSelect={handleSelect}
              numberOfMonths={2}
              month={displayMonth}
              onMonthChange={setDisplayMonth}
              disabled={{ after: TZDate.tz('UTC') }}
            />
          </div>
        </div>

        <div className="border-t p-4 flex items-center justify-between gap-4 min-h-[72px]">
          <DatePickerFooter
            dateRange={tempDate}
            endTime={endTime}
            startTime={startTime}
            handleTimeChange={handleTimeChange}
            handleApply={handleApply}
            handleClear={handleClear}
            primaryButtonLabel={primaryButtonLabel}
          />
        </div>
      </div>
    </GenericPopover>
  );
};
